import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Search, Loader2, CheckCircle2, XCircle, Mail, Clock, UserPlus, Send, Filter
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle },
  activated: { label: 'Activated', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: UserPlus },
};

export default function WaitlistManagement() {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchWaitlist(); }, []);

  const fetchWaitlist = async () => {
    try {
      const res = await api.get('/admin/waitlist');
      setRequests(res.data.requests || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error('Failed to load waitlist');
    } finally { setLoading(false); }
  };

  const handleApprove = async (requestId) => {
    setActionLoading(requestId + '_approve');
    try {
      await api.put(`/admin/waitlist/${requestId}/approve`);
      toast.success('Request approved! Invitation token generated.');
      fetchWaitlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (requestId) => {
    setActionLoading(requestId + '_reject');
    try {
      await api.put(`/admin/waitlist/${requestId}/reject`);
      toast.success('Request rejected');
      fetchWaitlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally { setActionLoading(null); }
  };

  const handleSendEmail = async (requestId) => {
    setActionLoading(requestId + '_email');
    try {
      const res = await api.post(`/admin/invite/send/${requestId}`);
      const { status, invite_url, email } = res.data;
      
      if (status === 'sent') {
        toast.success('Invitation email sent!');
      } else {
        // Email failed — show invite link for manual sharing
        toast.warning(
          <div className="space-y-2">
            <p className="font-medium">Email delivery failed. Copy and share this link manually:</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-secondary p-1.5 rounded break-all flex-1">{invite_url}</code>
              <button onClick={() => { navigator.clipboard.writeText(invite_url); toast.success('Link copied!'); }} className="shrink-0 text-xs text-primary underline">Copy</button>
            </div>
          </div>,
          { duration: 30000 }
        );
      }
      fetchWaitlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setActionLoading(null); }
  };

  const filtered = requests.filter(r => {
    const matchesSearch = !searchTerm ||
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.profession?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waitlist-management">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: counts.total, color: 'text-foreground' },
          { label: 'Pending', value: counts.pending, color: 'text-amber-400' },
          { label: 'Approved', value: counts.approved, color: 'text-blue-400' },
          { label: 'Rejected', value: counts.rejected, color: 'text-red-400' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-lg bg-secondary/30 border border-border/50 text-center">
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 font-mono ${s.color}`} data-testid={`waitlist-stat-${s.label.toLowerCase()}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Access Requests</CardTitle>
              <CardDescription>Review and manage platform access requests</CardDescription>
            </div>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="waitlist-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Filter */}
          <div className="flex gap-1 bg-secondary/30 rounded-lg p-1 w-fit">
            {['all', 'pending', 'approved', 'rejected', 'activated'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  statusFilter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`waitlist-filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Request List */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <UserPlus className="h-10 w-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                <p className="text-sm">No requests found</p>
              </div>
            ) : (
              filtered.map((req) => {
                const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.icon;
                return (
                  <motion.div
                    key={req.request_id}
                    className="p-4 rounded-lg bg-secondary/20 border border-border/50 hover:border-border/80 transition-colors"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`waitlist-item-${req.request_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Info */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-sm">{req.name}</h4>
                          <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {cfg.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{req.email}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{req.profession}</span>
                          <span>|</span>
                          <span>{req.phone}</span>
                        </div>
                        {req.interest_reason && (
                          <p className="text-xs text-muted-foreground/80 mt-1 line-clamp-2">{req.interest_reason}</p>
                        )}
                        {req.referred_by && (
                          <p className="text-[10px] text-muted-foreground/60 mt-1">Referred by: {req.referred_by}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground/50 mt-1">{formatDate(req.created_at)}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {req.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                              onClick={() => handleApprove(req.request_id)}
                              disabled={!!actionLoading}
                              data-testid={`approve-${req.request_id}`}
                            >
                              {actionLoading === req.request_id + '_approve' ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                              onClick={() => handleReject(req.request_id)}
                              disabled={!!actionLoading}
                              data-testid={`reject-${req.request_id}`}
                            >
                              {actionLoading === req.request_id + '_reject' ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <><XCircle className="h-3.5 w-3.5 mr-1" /> Reject</>
                              )}
                            </Button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                            onClick={() => handleSendEmail(req.request_id)}
                            disabled={!!actionLoading}
                            data-testid={`send-email-${req.request_id}`}
                          >
                            {actionLoading === req.request_id + '_email' ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <><Send className="h-3.5 w-3.5 mr-1" /> Send Invite</>
                            )}
                          </Button>
                        )}
                        {req.status === 'activated' && (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            Account Created
                          </Badge>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
